
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Plus, MoreHorizontal, Mail, Clock, CheckCircle, XCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import CreateUserModal from './CreateUserModal';
import OrphanedUserFixButton from './OrphanedUserFixButton';

type CombinedItemStatus = 'active' | 'pending';

interface CombinedDataItem {
  type: 'user' | 'invitation';
  id: string;
  email: string;
  name: string;
  status: CombinedItemStatus;
  role: string;
  created_at: string;
  last_activity?: string;
  user?: any;
  invitation?: any;
}

const EnhancedUserManagementTab: React.FC = () => {
  const { isCompanyAdmin, isSuperAdmin } = useAuth();
  const {
    users,
    invitations,
    loading,
    fetchUsers,
    inviteUser,
    updateUserRole,
    removeUserFromCompany,
    resendInvitation,
    revokeInvitation
  } = useUserManagement();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const isOwner = isCompanyAdmin();
  const isManager = isCompanyAdmin(); // Company admins can manage
  const canAssignManager = isOwner;

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Combine users and invitations into a unified list - filter out used invitations
  const combinedData: CombinedDataItem[] = [
    // Add active users
    ...users.map(user => ({
      type: 'user' as const,
      id: user.id,
      email: user.email,
      name: user.full_name || 'No name',
      status: 'active' as CombinedItemStatus,
      role: user.role,
      created_at: user.created_at,
      last_activity: user.last_activity,
      user: user
    })),
    // Add only pending invitations (exclude used ones)
    ...invitations
      .filter(invitation => invitation.used_at === null || invitation.used_at === undefined)
      .map(invitation => {
        const status: CombinedItemStatus = 'pending';
        
        return {
          type: 'invitation' as const,
          id: invitation.id,
          email: invitation.email,
          name: '',
          status,
          role: 'pending',
          created_at: invitation.created_at,
          invitation: invitation
        };
      })
  ];

  // Filter combined data based on search term and status
  const filteredData = combinedData.filter(item => {
    const matchesSearch = item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      matchesStatus = item.status === statusFilter;
    }
    
    return matchesSearch && matchesStatus;
  });

  const handleInviteUser = async (userData: { email: string; role: string }) => {
    return await inviteUser(userData);
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner': return 'default';
      case 'admin': return 'secondary';
      case 'member': return 'outline';
      case 'pending': return 'outline';
      default: return 'outline';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      case 'member': return 'Member';
      case 'pending': return 'Pending';
      default: return role;
    }
  };

  const getStatusBadge = (status: CombinedItemStatus) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Active
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-black">User Management</h3>
          <p className="text-sm text-muted-foreground">Manage users and invitations for your organization</p>
        </div>
        <Button 
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search users and invitations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users & Invitations ({filteredData.length})</CardTitle>
          <CardDescription>
            All users and pending invitations in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || statusFilter !== 'all' ? 'No results match your filters' : 'No users or invitations found'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name / Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <TableRow key={`${item.type}-${item.id}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.name || item.email}</div>
                        {item.name && <div className="text-sm text-muted-foreground">{item.email}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(item.status)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(item.role)}>
                        {getRoleDisplayName(item.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(item.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {item.type === 'user' 
                        ? (item.last_activity 
                            ? new Date(item.last_activity).toLocaleDateString()
                            : 'Never'
                          )
                        : 'Pending'
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.type === 'invitation' && (
                          <OrphanedUserFixButton
                            email={item.email}
                            invitationId={item.id}
                            isUsed={false}
                          />
                        )}
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {item.type === 'user' && (
                              <>
                                {(isOwner || isManager) && item.user.role !== 'owner' && (
                                  <DropdownMenuItem
                                    onClick={() => updateUserRole(item.user.id, 'admin')}
                                    disabled={item.user.role === 'admin'}
                                  >
                                    Make Admin
                                  </DropdownMenuItem>
                                )}
                                {isOwner && item.user.role !== 'owner' && (
                                  <DropdownMenuItem
                                    onClick={() => updateUserRole(item.user.id, 'owner')}
                                  >
                                    Make Owner
                                  </DropdownMenuItem>
                                )}
                                {(isOwner || isManager) && item.user.role !== 'member' && (
                                  <DropdownMenuItem
                                    onClick={() => updateUserRole(item.user.id, 'member')}
                                  >
                                    Make Member
                                  </DropdownMenuItem>
                                )}
                                {(isOwner || isManager) && (
                                  <DropdownMenuItem
                                    onClick={() => removeUserFromCompany(item.user.id)}
                                    className="text-red-600"
                                  >
                                    Remove from Company
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            
                            {item.type === 'invitation' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => resendInvitation(item.id)}
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Resend
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => revokeInvitation(item.id)}
                                  className="text-red-600"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Revoke
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateUserModal
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
        onInviteUser={handleInviteUser}
        isOwner={isOwner}
        canAssignManager={canAssignManager}
      />
    </div>
  );
};

export default EnhancedUserManagementTab;
